import { snakeCase } from 'lodash';
import type { GtmEntryElement } from '../gtm-entry-element.enum';
import { Gtm } from '../gtmClass';

export enum GtmRecipeLocation {
  MainFloatingBtn = 'mainFloatingBtn', // 메인 플로팅 버튼
  DetailFloatingBtn = 'detailFloatingBtn', // 디테일 플로팅버튼
  CreatorTabBarBtn = 'creatorTabBarBtn', // 크리에이터 탭바버튼
  CreatorFloatingBtn = 'creatorFloatingBtn', // 크리에이터 플로팅버튼
  MyPageCommunityTab = 'mypageCommunityTab', // 마이페이지 커뮤니티 탭
  MyPageCreateReviewModal = 'mypageCreateReviewModal', // 마이페이지 리뷰 작성 모달
  MyPagePromotionModal = 'mypagePromotionModal', // 마이페이지 프로모션 모달
}

type PostType = 'recipe' | 'cook_diary' | 'blog';

export class CommunityGtm extends Gtm {
  static category = 'recipes';

  /** 커뮤니티 페이지 방문 */
  visit_community_page() {
    this.track('visit_community_page');
  }

  /** 커뮤니티 페이지로 이동 */
  click_go_to_community(props: { path: '/subscriptions/before-pause/2' }) {
    this.track('click_go_to_community', props);
  }

  /** 마이 다이어리 페이지 방문 */
  visit_my_diary_page(props: {
    path: '/mypages/creator' | '/recipes/bookmark';
  }) {
    this.track('visit_my_diary_page', props);
  }

  /** 집밥일기 상세 페이지 진입 */
  visit_cook_diary_detail_page(props: { recipe_diary_id: string }) {
    this.track('visit_cook_diary_detail_page', { ...props, path: this.path });
  }

  /** 마이 다이어리 클릭 */
  click_my_diary() {
    this.track('click_my_diary', { path: this.path });
  }

  /** 좋아요 클릭 */
  click_like(props: { post_type: PostType; post_id: string }) {
    this.track('click_like', { ...props, path: this.path });
  }

  /** 북마크 클릭 — 누른 화면은 현재 경로로 구분한다(상세 시트도 뒤 화면 경로가 그대로다) */
  click_bookmark(props: { post_type: PostType; post_id: string }) {
    this.track('click_bookmark', { ...props, path: this.path });
  }

  /** 글 상세 페이지 방문 */
  visit_post_detail_page(props: {
    post_type: PostType;
    /** 글 아이디 */
    post_id: string;
    /** 상세를 연 화면 — 마이냉장고는 페이지 이동 없이 바텀시트로 열어 이 값으로 구분한다 */
    path: '/recipes' | '/recipes/diary' | '/blogs/post' | '/refrigerator';
  }) {
    this.track('visit_post_detail_page', { ...props });
  }

  /** 글 클릭 */
  click_post(props: {
    post_type: 'blog';
    /** 글 아이디 */
    post_id: string;
    /**
     * 글 작성자
     * admin (우리가 만든 레시피) 또는 user (유저가 만든 레시피
     */
    created_by: 'admin' | 'user';
    /** 발화 구좌 — 정기배송 홈 진입 칩에서 눌렀는지 구분한다 */
    element?: GtmEntryElement;
  }) {
    this.track('click_post', { ...props, path: this.path });
  }

  /** 레시피 클릭 */
  click_recipe(props: {
    recipe_id: string;
    recipe_name: string;
    /**
     * 글 작성자
     * admin (우리가 만든 레시피) 또는 user (유저가 만든 레시피
     */
    created_by: 'admin' | 'user';
  }) {
    this.track('click_recipe', { ...props, path: this.path });
  }

  /** 집밥일기 클릭 */
  click_cook_diary(props: { recipe_diary_id: string }) {
    this.track('click_cook_diary', { ...props, path: this.path });
  }

  /** 글 작성 완료 버튼 클릭 */
  complete_create_post(props: { post_type: PostType }) {
    this.track('complete_create_post', { ...props });
  }

  /** 글 작성 페이지 방문 */
  visit_create_post_page(props: { post_type: PostType }) {
    this.track('visit_create_post_page', { ...props, path: this.path });
  }

  /** 프로필 생성 완료 버튼 클릭 */
  complete_create_profile() {
    this.track('complete_create_profile');
  }

  /** 프로필 생성 페이지 방문 */
  visit_create_profile_page() {
    this.track('visit_create_profile_page');
  }

  /** 글 업로드 버튼 클릭 */
  click_upload_post(props: {
    /** 글 종류 */
    post_type: PostType;
    /** 버튼 클릭 페이지 */
    source: GtmRecipeLocation;
  }) {
    this.track('click_upload_post', {
      post_type: props.post_type,
      source: snakeCase(props.source),
      path: this.path,
    });
  }

  get util() {
    return {
      created_by({ authorName }: { authorName: string }): 'admin' | 'user' {
        return authorName === '어글리어스마켓' ? 'admin' : 'user';
      },
    };
  }
}
